import { config } from "../config";
import { duviSharedAdminNotifySessionExpired } from "@duvi-agency/auth";
import type {
  DuviSharedAdminNicheSummary,
  DuviSharedAdminNicheAccountIds,
} from "@duvi-agency/api-client";

// Local aliases (and re-exports under the pre-existing names) so existing
// imports (`from "../api/client"`) and in-file usages keep working without
// further changes. (Own-export DuviCoreAdmin* prefixing is story 13.)
type NicheSummary = DuviSharedAdminNicheSummary;
type NicheAccountIds = DuviSharedAdminNicheAccountIds;
export type { NicheSummary, NicheAccountIds };

export interface Envelope<T> {
  data: T;
  errors?: Array<{ code: string; message: string; field?: string }>;
  meta?: { total_count?: number; cursor?: string | null };
  _links?: Record<string, { href: string; method?: string }>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// --- Token refresh mutex ---
// Prevents multiple concurrent refresh attempts when several requests
// hit 401 simultaneously.
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  try {
    // POST /refresh with no body — the __Secure-duvi-rt HttpOnly cookie is
    // sent automatically via credentials: "include".
    const res = await fetch(`${config.authUrl}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (!res.ok) {
      console.error(
        "[auth] refresh failed",
        res.status,
        await res.text().catch(() => ""),
      );
      return false;
    }

    console.debug("[auth] token refreshed successfully");
    return true;
  } catch (err) {
    console.error("[auth] refresh error", err);
    return false;
  }
}

async function refreshTokenOnce(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = tryRefreshToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doFetch(
  url: string,
  method: string,
  headers: Record<string, string>,
  body?: unknown,
): Promise<Response> {
  // No Authorization header — the HttpOnly access-token cookie (__Host-duvi-at)
  // is sent automatically via credentials: "include".
  return fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  params?: Record<string, string | number | boolean | undefined>,
): Promise<Envelope<T>> {
  // config.apiUrl may be relative (e.g. "/api" — same-origin per spec 013) or
  // absolute. Resolve against window.location.origin so URL() works either way.
  const url = new URL(`${config.apiUrl}${path}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  let res = await doFetch(url.toString(), method, headers, body);

  // On 401 or 403 (API Gateway returns 403 when authoriser denies),
  // attempt a silent token refresh and retry once.
  if (res.status === 401 || res.status === 403) {
    console.debug(
      `[auth] ${res.status} on ${method} ${path}, attempting token refresh`,
    );
    const refreshed = await refreshTokenOnce();
    if (refreshed) {
      res = await doFetch(url.toString(), method, headers, body);
    }
  }

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      // Refresh failed or retried request still unauthorized — session is dead.
      console.error(
        `[auth] session expired: ${res.status} on ${method} ${path} after refresh attempt`,
      );
      duviSharedAdminNotifySessionExpired();
      throw new ApiError(
        res.status,
        "SESSION_EXPIRED",
        "Your session has timed out. Please sign in again.",
      );
    }
    const errorBody = await res.json().catch(() => ({
      errors: [{ code: "unknown", message: res.statusText }],
    }));
    const err = errorBody.errors?.[0] ?? {
      code: "unknown",
      message: res.statusText,
    };
    throw new ApiError(res.status, err.code, err.message);
  }

  return res.json();
}

export const api = {
  get: <T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
  ) => request<T>("GET", path, undefined, params),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

// --- Dashboard ---
export interface DashboardData {
  open_platform_tickets: number;
  audit_events_last_24h: number;
  total_users: number;
  generated_at: string;
}

// --- KB ---
export interface KBArticle {
  article_id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
  status: "published" | "draft" | "archived";
  updated_at: string;
}

// --- Support Tickets ---
export interface Ticket {
  ticket_id: string;
  submitter_id: string;
  submitter_role: string;
  category: string;
  priority: string;
  status: string;
  assigned_level: string;
  subject: string;
  created_at: string;
  updated_at: string;
}

export interface TicketMessage {
  msg_id: string;
  author_id: string;
  author_role: string;
  content: string;
  is_internal?: boolean;
  created_at: string;
}

export interface TicketDetail {
  ticket: Ticket;
  messages: TicketMessage[];
}

// --- Users ---
export interface PlatformUser {
  user_id: string;
  email: string;
  name: string;
  status: string;
  avatar_url?: string;
  mfa_enabled?: boolean;
  created_at: string;
}

export type PlatformUserDetail = PlatformUser;

// --- Audit ---
export interface AuditEvent {
  event_id: string;
  timestamp: string;
  actor_id: string;
  actor_name: string;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_name?: string;
  detail?: string;
  ip_address?: string;
  user_agent?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

// --- Niches (Platform — core-platform.yaml v1.2.0) ---
// `NicheSummary` and `NicheAccountIds` are sourced from
// `@duvi-agency/api-client` (mirrored from the OpenAPI contract) and
// re-exported above for backwards-compatible local imports.

// --- Notifications ---
export interface Notification {
  notif_id: string;
  type: string;
  title: string;
  summary: string;
  link?: string;
  read: boolean;
  created_at: string;
}

// --- Current user profile (core-api) ---
export interface MyProfile {
  user_id: string;
  email: string;
  name: string;
  avatar_url?: string;
  mfa_enabled: boolean;
}

export interface SessionInfo {
  session_id: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  expires_at: number;
  current: boolean;
}

export const me = {
  get: () => api.get<MyProfile>("/users/me"),
  updateName: (name: string) => api.put<void>("/users/me/name", { name }),
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    // No Authorization header — the HttpOnly cookie is sent automatically.
    return fetch(`${config.apiUrl}/users/me/avatar`, {
      method: "PUT",
      credentials: "include",
      body: formData,
    }).then((res) => {
      if (!res.ok)
        throw new ApiError(
          res.status,
          "UPLOAD_FAILED",
          "Failed to upload avatar",
        );
      return res.json() as Promise<Envelope<{ avatar_url: string }>>;
    });
  },
  removeAvatar: () => api.delete<void>("/users/me/avatar"),
  listSessions: () => api.get<SessionInfo[]>("/users/me/sessions"),
  logoutAllSessions: () => api.post<void>("/users/me/sessions/logout-all"),
};

// --- API functions ---
export const dashboard = {
  get: () => api.get<DashboardData>("/dashboard"),
};

export const kb = {
  list: (params?: {
    cursor?: string;
    limit?: number;
    category?: string;
    search?: string;
  }) => api.get<KBArticle[]>("/knowledge-base/articles", params),
  get: (id: string) => api.get<KBArticle>(`/knowledge-base/articles/${id}`),
  create: (data: {
    title: string;
    content: string;
    category: string;
    tags?: string[];
  }) => api.post<KBArticle>("/knowledge-base/articles", data),
  update: (
    id: string,
    data: Partial<{
      title: string;
      content: string;
      category: string;
      tags: string[];
    }>,
  ) => api.put<KBArticle>(`/knowledge-base/articles/${id}`, data),
  delete: (id: string) => api.delete<void>(`/knowledge-base/articles/${id}`),
};

export const roadmap = {
  push: (data: {
    title: string;
    description: string;
    status: string;
    quarter_label?: string;
  }) => api.post<void>("/roadmap/push", data),
};

export const tickets = {
  list: (params?: {
    cursor?: string;
    limit?: number;
    status?: string;
    priority?: string;
    category?: string;
    search?: string;
  }) => api.get<Ticket[]>("/support/tickets", params),
  get: (id: string) => api.get<TicketDetail>(`/support/tickets/${id}`),
  addMessage: (id: string, data: { content: string; is_internal?: boolean }) =>
    api.post<TicketMessage>(`/support/tickets/${id}/messages`, data),
  resolve: (id: string, notes?: string) =>
    api.post<void>(
      `/support/tickets/${id}/resolve`,
      notes ? { resolution_notes: notes } : undefined,
    ),
  close: (id: string) => api.post<void>(`/support/tickets/${id}/close`),
};

export const users = {
  list: () => api.get<PlatformUser[]>("/users"),
  get: (id: string) => api.get<PlatformUserDetail>(`/users/${id}`),
  invite: (data: { email: string; note?: string }) =>
    api.post<void>("/users/invite", data),
  disable: (id: string) => api.post<void>(`/users/${id}/disable`),
  enable: (id: string) => api.post<void>(`/users/${id}/enable`),
  archive: (id: string) => api.post<void>(`/users/${id}/archive`),
  unarchive: (id: string) => api.post<void>(`/users/${id}/unarchive`),
  forceResetPassword: (id: string) =>
    api.post<void>(`/users/${id}/force-reset-password`),
};

export const niches = {
  list: (params?: { cursor?: string; limit?: number }) =>
    api.get<NicheSummary[]>("/platform/niches", params),
};

export const audit = {
  list: (params?: {
    cursor?: string;
    limit?: number;
    actor?: string;
    action?: string;
    entity_type?: string;
    date_from?: string;
    date_to?: string;
  }) => api.get<AuditEvent[]>("/audit/events", params),
};

export const notifications = {
  list: (params?: { cursor?: string; limit?: number }) =>
    api.get<Notification[]>("/notifications", params),
  unreadCount: () =>
    api.get<{ unread_count: number }>("/notifications/unread-count"),
  markRead: (id: string) => api.post<void>(`/notifications/${id}/read`),
};
